import axios from "axios";
import { ClassValue, clsx } from "clsx";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import { getAudience, getConfig } from "@/components/config";
import {
  setButtonBackToActive,
  updateActionSummaryForESID,
} from "@/store/tradeDataStoreACTIONS";
import tradeCreationInProcessStore from "@/store/tradeCreationInProcessStore";

export function concatenateClassnames(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const config = getConfig();

interface FetchRequestWrapperParams {
  url: string;
  method?: string;
  objToSend?: any;
  ignoreResponse?: boolean;
  // Opt-in per call. No default: the reference-data pulls are large and slow, and a
  // blanket timeout would fail them (LQC-2676).
  timeoutMs?: number;
}

export const fetchRequestWrapper = async (
  {
    url,
    method = "GET",
    objToSend,
    ignoreResponse = false,
    timeoutMs,
  }: FetchRequestWrapperParams,
  getAccessTokenSilently: any,
): Promise<any> => {
  try {
    const tokenformPassedFunction = await getAccessTokenSilently({
      authorizationParams: {
        audience: `${getAudience()?.audience}`,
        scope: "openid profile email",
      },
    });

    const headers: HeadersInit = {
      Authorization: `Bearer ${tokenformPassedFunction}`,
      "Content-Type": "application/json",
    };
    interface IAxiosConfig {
      method: string;
      url: string;
      headers: {};
      data?: string;
      timeout?: number;
    }

    url = `${config.baseUrl}${url}`;

    const axiosConfig: IAxiosConfig = {
      method,
      url,
      headers,
    };

    if (objToSend) {
      axiosConfig.data = JSON.stringify(objToSend);
    }
    if (timeoutMs) {
      axiosConfig.timeout = timeoutMs;
    }

    const response = await axios(axiosConfig);

    if (ignoreResponse) {
      return;
    }
    if (objToSend && objToSend.esid) {
      setButtonBackToActive(objToSend.esid);
    }
    return response.data;
  } catch (error: any) {
    const JSONString = JSON.stringify(error, null, 2);
    const JSONErrorMsg = JSON.parse(JSONString);

    if (
      JSONErrorMsg &&
      JSONErrorMsg.error &&
      JSONErrorMsg.error == "login_required"
    ) {
      // Toast notification coming up when the user has not performed an action for the time out period.
    } else if (error.code === "ECONNABORTED") {
      // Axios aborts a request past its timeout. The server may still have processed it,
      // so this says to check rather than to resend.
      toast.error(
        "The request timed out. It may still have gone through - check before resending.",
      );
    } else if (error.status == 403) {
      toast.error(
        "Access denied. Read Only users cannot perform this operation.",
      );
    } else {
      console.error("Error making the request:", error);
      toast.error("An error has occurred with this action.");
      if (objToSend) {
        if ("esid" in objToSend) {
          updateActionSummaryForESID(objToSend.esid, getAccessTokenSilently);
        }
      }
    }
    throw error; // rethrow the error to handle it where the function is called
  } finally {
    if (objToSend) {
      // ESID present means trade data.
      if (objToSend.esid) {
        setButtonBackToActive(objToSend.esid);
      } else if (
        url.includes("/Blocks/Recreate") ||
        url.includes("/Trade/Execution/Save")
      ) {
        // Sets the trade creation buttons back active if either of the related endpoints are attempted.
        tradeCreationInProcessStore.getState().setIsTradeCreationBusy(false);
      }
    }
  }
};

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay ?? 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function formatNumberWithCommas(number: number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function addPeriodAfterSix(number: number) {
  const numberString = number.toString();
  const sliceFrom = numberString.length - 6;
  const integerPart = numberString.slice(0, sliceFrom);
  const decimalPart = numberString.slice(sliceFrom);
  return `${integerPart}.${decimalPart}`;
}

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

export function delayViaPromise(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function capitaliseFirstLetter(string: string) {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}
